/**
 * Daily habit (pure): "Today's drawing" (a deterministic pick per day and profile that adapts to the
 * child's level and favourite subjects), a gentle weekly rhythm (a plant that grows with each drawing
 * of the week and blooms at the goal — missed weeks never wilt anything), the garden of past weeks,
 * kind reminder copy and the month grid of the drawing calendar.
 */
import type { PathId } from '../content/model';
import { hash } from '../mailing';
import { localize, t, type L, type Lang } from '../text';
import { pickLocale, type LocaleTable } from '../i18n/locales';

/** Greeting word used when the child's name is unknown. */
const DEFAULT_NAME: LocaleTable<string> = { en: 'Hi', vi: 'Con', 'zh-Hans': '小画家' };

const pad = (n: number) => String(n).padStart(2, '0');
/** Local calendar day YYYY-MM-DD. */
export const habitDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1, 12);
};
export const habitAddDays = (day: string, n: number) => {
  const d = parseDay(day);
  d.setDate(d.getDate() + n);
  return habitDay(d);
};
export const habitDaysBetween = (a: string, b: string) => Math.round((parseDay(b).getTime() - parseDay(a).getTime()) / 86400_000);
/** Monday of the week containing `day` (weeks start on Monday). */
export const habitWeekOf = (day: string) => habitAddDays(day, -((parseDay(day).getDay() + 6) % 7));

/* -------------------------------- Today's drawing -------------------------------- */

export const DAILY_MINUTES: [number, number] = [5, 10];
/** Subjects drawn this many days ago or less are not suggested again (unless nothing else is left). */
export const DAILY_RECENT_DAYS = 14;

export interface DailyCandidate {
  drawingId: string;
  /** Playground item that plays it. */
  itemId: string;
  category: string;
  world: PathId;
  minutes: number;
  /** Difficulty band 1..3. */
  difficulty: number;
  /** Playable without a subscription. */
  free: boolean;
}

export type DailyReason = 'favourite' | 'stretch' | 'fresh' | 'again';

export interface DailyPick {
  day: string;
  candidate: DailyCandidate;
  reason: DailyReason;
}

/** Comfortable difficulty for a sparks level (1..7): 1-2 → 1, 3-4 → 2, 5+ → 3. */
export const comfortDifficulty = (level: number) => (level >= 5 ? 3 : level >= 3 ? 2 : 1);

/**
 * Today's drawing for a profile: deterministic per (day, seed) so it stays the same all day on every
 * device, short (5–10 min), never something drawn in the last two weeks, mostly at the child's
 * comfortable difficulty with a gentle stretch now and then, and weighted towards favourite subjects.
 */
export function todaysDrawing(input: {
  day: string;
  seed: string;
  candidates: DailyCandidate[];
  level: number;
  favourites: readonly string[];
  /** Drawing ids with the day they were last drawn (YYYY-MM-DD). */
  lastDrawn: Readonly<Record<string, string>>;
  premium: boolean;
}): DailyPick | null {
  const { day, candidates } = input;
  const playable = candidates.filter((c) => input.premium || c.free);
  const pool0 = playable.length ? playable : candidates;
  const short = pool0.filter((c) => c.minutes >= DAILY_MINUTES[0] && c.minutes <= DAILY_MINUTES[1]);
  const pool1 = short.length ? short : pool0;
  const notRecent = pool1.filter((c) => {
    const last = input.lastDrawn[c.drawingId];
    return !last || habitDaysBetween(last, day) > DAILY_RECENT_DAYS;
  });
  const pool = notRecent.length ? notRecent : pool1;
  if (!pool.length) return null;
  const comfort = comfortDifficulty(input.level);
  const fav = new Set(input.favourites);
  // One day in three is a "stretch" day (one band harder), the rest stay comfortable.
  const stretch = hash(`stretch:${day}:${input.seed}`) % 3 === 0 && comfort < 3;
  const target = stretch ? comfort + 1 : comfort;
  const weight = (c: DailyCandidate) => {
    const gap = Math.abs(c.difficulty - target);
    let w = gap === 0 ? 6 : gap === 1 ? 2 : 0.5;
    if (fav.has(c.category)) w *= 3;
    if (!input.lastDrawn[c.drawingId]) w *= 1.5;
    return w;
  };
  const sorted = [...pool].sort((a, b) => a.drawingId.localeCompare(b.drawingId));
  const total = sorted.reduce((n, c) => n + weight(c), 0);
  let roll = ((hash(`daily:${day}:${input.seed}`) % 10_000) / 10_000) * total;
  let pick = sorted[sorted.length - 1]!;
  for (const c of sorted) {
    roll -= weight(c);
    if (roll < 0) {
      pick = c;
      break;
    }
  }
  const reason: DailyReason = input.lastDrawn[pick.drawingId] ? 'again' : fav.has(pick.category) ? 'favourite' : stretch && pick.difficulty > comfort ? 'stretch' : 'fresh';
  return { day, candidate: pick, reason };
}

/* --------------------------------- Weekly rhythm --------------------------------- */

export const DEFAULT_WEEKLY_GOAL = 3;
export const WEEKLY_GOAL_CHOICES = [2, 3, 4, 5] as const;

/** 0 seed · 1 sprout · 2 bud · 3 bloom. */
export type PlantStage = 0 | 1 | 2 | 3;
export const plantStage = (count: number, goal: number): PlantStage => (count >= goal ? 3 : count <= 0 ? 0 : count / goal < 0.5 ? 1 : 2);

export interface WeekRhythm {
  weekStart: string;
  count: number;
  goal: number;
  stage: PlantStage;
  bloomed: boolean;
  /** Drawings beyond the goal (butterflies visiting the flower), max 3. */
  extra: number;
  /** Days left in the week including today. */
  daysLeft: number;
  /** Distinct days with a drawing this week. */
  activeDays: string[];
}

/** This week's rhythm. `days` = the local day of every drawing (one entry per drawing). */
export function weekRhythm(days: readonly string[], today: string, goal = DEFAULT_WEEKLY_GOAL): WeekRhythm {
  const start = habitWeekOf(today);
  const end = habitAddDays(start, 6);
  const mine = days.filter((d) => d >= start && d <= end);
  const count = mine.length;
  return {
    weekStart: start,
    count,
    goal,
    stage: plantStage(count, goal),
    bloomed: count >= goal,
    extra: Math.min(3, Math.max(0, count - goal)),
    daysLeft: habitDaysBetween(today, end) + 1,
    activeDays: [...new Set(mine)].sort(),
  };
}

export interface GardenWeek {
  weekStart: string;
  count: number;
  stage: PlantStage;
  bloomed: boolean;
}

/**
 * The garden: the last `weeks` weeks (oldest first, current week last) and the all-time number of
 * bloomed weeks. Weeks without drawings stay as a seed resting in the soil — nothing ever wilts.
 */
export function habitGarden(days: readonly string[], today: string, goal = DEFAULT_WEEKLY_GOAL, weeks = 8): { weeks: GardenWeek[]; blooms: number } {
  const counts = new Map<string, number>();
  for (const d of days) counts.set(habitWeekOf(d), (counts.get(habitWeekOf(d)) ?? 0) + 1);
  const cur = habitWeekOf(today);
  const out: GardenWeek[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const ws = habitAddDays(cur, -7 * i);
    const c = counts.get(ws) ?? 0;
    out.push({ weekStart: ws, count: c, stage: plantStage(c, goal), bloomed: c >= goal });
  }
  const blooms = [...counts.entries()].filter(([ws, c]) => ws <= cur && c >= goal).length;
  return { weeks: out, blooms };
}

/* -------------------------------- Reminder copy -------------------------------- */

export interface ReminderCopyInput {
  lang: Lang;
  name: string;
  day: string;
  goal: number;
  /** Drawings so far this week. */
  weekCount: number;
  /** Days since the last drawing (null = never drew). */
  daysSinceLast: number | null;
  /** Today's drawing title and minutes, when known. */
  prompt?: { title: L | string; minutes: number } | null;
}

type Tpl = { title: L; body: L };
const T = (en: string, vi: string): L => t(en, vi); // t() registers the strings for the i18n catalogs

const COPY: Record<'bloomed' | 'almost' | 'welcomeBack' | 'prompt' | 'gentle', Tpl[]> = {
  bloomed: [
    { title: T("{name}'s flower is blooming!", 'Bông hoa của {name} đã nở!'), body: T("You reached this week's drawing goal. Fancy a doodle just for fun?", 'Con đã đạt mục tiêu vẽ của tuần này. Vẽ thêm một tranh cho vui nhé?') },
    { title: T('A butterfly is visiting your garden', 'Có bướm ghé thăm khu vườn của con'), body: T('Every extra drawing brings a new friend to your flower.', 'Mỗi tranh vẽ thêm lại có một người bạn mới ghé bông hoa của con.') },
  ],
  almost: [
    { title: T('One more drawing to bloom', 'Thêm một tranh là hoa nở'), body: T("{name}, your plant has a bud. One drawing and it opens!", '{name} ơi, cây của con đã có nụ rồi. Một tranh nữa là hoa nở!') },
  ],
  welcomeBack: [
    { title: T('Scribble saved you a page', 'Scribble giữ cho con một trang giấy'), body: T('{prompt} is waiting, about {min} minutes. No rush!', '{prompt} đang chờ con, chỉ khoảng {min} phút. Cứ thong thả nhé!') },
    { title: T('Your pencils miss you', 'Bút chì nhớ con rồi đó'), body: T('Draw {prompt} with Juno today, about {min} minutes.', 'Hôm nay vẽ {prompt} cùng cô Juno nhé, khoảng {min} phút.') },
  ],
  prompt: [
    { title: T("Today's drawing: {prompt}", 'Tranh hôm nay: {prompt}'), body: T('Grab a pencil and some paper, about {min} minutes.', 'Lấy bút chì và giấy thôi, chỉ khoảng {min} phút.') },
    { title: T('{name}, a new drawing is ready', '{name} ơi, có tranh mới rồi'), body: T("Today it's {prompt}. Let's draw it together!", 'Hôm nay mình vẽ {prompt}. Cùng vẽ nhé!') },
    { title: T('Time for a little drawing', 'Đến giờ vẽ một chút rồi'), body: T('{prompt} takes about {min} minutes. Your plant will love it.', '{prompt} chỉ mất khoảng {min} phút. Cây của con sẽ thích lắm.') },
  ],
  gentle: [
    { title: T('Drawing time with Scribble', 'Giờ vẽ cùng Scribble'), body: T('A few minutes of drawing makes your garden grow.', 'Vài phút vẽ tranh là khu vườn của con lớn thêm.') },
  ],
};

/**
 * Kind, never-guilt reminder copy. Picks the situation (goal met, one drawing from the goal, coming
 * back after a break, today's prompt) and a deterministic variant per day.
 */
export function reminderCopy(input: ReminderCopyInput): { title: string; body: string; kind: keyof typeof COPY } {
  const { lang } = input;
  const kind: keyof typeof COPY =
    input.weekCount >= input.goal
      ? 'bloomed'
      : input.weekCount === input.goal - 1 && input.weekCount > 0
        ? 'almost'
        : !input.prompt
          ? 'gentle'
          : input.daysSinceLast === null || input.daysSinceLast >= 5
            ? 'welcomeBack'
            : 'prompt';
  const list = COPY[kind];
  const tpl = list[hash(`remind:${input.day}`) % list.length]!;
  const vars: Record<string, string> = {
    name: input.name || pickLocale(DEFAULT_NAME, lang),
    prompt: input.prompt ? localize(input.prompt.title, lang) : '',
    min: String(input.prompt?.minutes ?? 7),
  };
  const fillIn = (s: string) => s.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '');
  return { title: fillIn(localize(tpl.title, lang)), body: fillIn(localize(tpl.body, lang)), kind };
}

/* ------------------------------------ Calendar ------------------------------------ */

/** Month grid (weeks start on Monday): rows of 7 day keys, `null` outside the month. */
export function monthGrid(year: number, month: number): (string | null)[][] {
  const first = new Date(year, month, 1, 12);
  const lead = (first.getDay() + 6) % 7;
  const daysIn = new Date(year, month + 1, 0, 12).getDate();
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= daysIn; d++) cells.push(habitDay(new Date(year, month, d, 12)));
  while (cells.length % 7) cells.push(null);
  const rows: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  return rows;
}
